import React, { useState, useEffect, ReactNode, forwardRef, useImperativeHandle, memo } from "react";
import { mergeProps, NativeProps, withNativeProps } from "@/utils/components-utils";
import classNames from "classnames";
import Popup, { PopupProps } from "../popup-v2";
import { PickerColumn, PickerColumnItem, PickerValue, PickerValueExtend } from "./index";
import PickerView from "../picker-view-v2";
import { generateColumnsExtend, useColumnsExtend } from "../picker-view/columns-extend";
import SafeArea from "../safe-area";

import styles from "./picker.module.scss";
import useMemoizedFn from "@/hooks/useMemoizedFn";
import { usePropsValue } from "@/hooks/usePropsValue";
import { useTranslation } from "next-i18next";
import Button from "../Button";

export type PickerActions = {
	open: () => void;
	close: () => void;
	toggle: () => void;
};
export type PickerRef = PickerActions;

export type PickerProps = {
	columns: PickerColumn[] | ((value: PickerValue[]) => PickerColumn[]);
	value?: PickerValue[];
	defaultValue?: PickerValue[];
	loading?: boolean;
	loadingContent?: ReactNode;
	onSelect?: (value: PickerValue[], extend: PickerValueExtend) => void;
	onConfirm?: (value: PickerValue[], extend: PickerValueExtend) => void;
	onCancel?: () => void;
	onClose?: () => void;
	closeOnMaskClick?: boolean;
	visible?: boolean;
	title?: ReactNode;
	confirmText?: ReactNode;
	cancelText?: ReactNode;
	children?: (items: (PickerColumnItem | null)[], actions: PickerActions) => ReactNode;
	renderLabel?: (item: PickerColumnItem) => ReactNode;
	mouseWheel?: boolean;
	popupClassName?: string;
	popupStyle?: React.CSSProperties;
} & Pick<
	PopupProps,
	"getContainer" | "afterShow" | "afterClose" | "onClick" | "stopPropagation" | "forceRender" | "destroyOnClose"
> &
	NativeProps;

const defaultProps = {
	defaultValue: [],
	closeOnMaskClick: true,
	renderLabel: (item: PickerColumnItem) => item.label,
	destroyOnClose: false,
	forceRender: false,
};

export const Picker = memo(
	forwardRef<PickerRef, PickerProps>((p, ref) => {
		const { t } = useTranslation("common");
		const props = mergeProps(
			defaultProps,
			{
				confirmText: t("confirm", { ns: "common" }),
				cancelText: t("cancel", { ns: "common" }),
			},
			p,
		);

		const [visible, setVisible] = usePropsValue({
			value: props.visible,
			defaultValue: false,
			onChange: (v) => {
				if (v === false) {
					props.onClose?.();
				}
			},
		});

		const actions: PickerActions = {
			toggle: () => {
				setVisible((v) => !v);
			},
			open: () => {
				setVisible(true);
			},
			close: () => {
				setVisible(false);
			},
		};

		useImperativeHandle(ref, () => actions);

		const [value, setValue] = usePropsValue({
			...props,
			onChange: (val) => {
				const extend = generateColumnsExtend(props.columns, val);
				props.onConfirm?.(val, extend);
			},
		});

		const extend = useColumnsExtend(props.columns, value);

		const [innerValue, setInnerValue] = useState<PickerValue[]>(value);
		useEffect(() => {
			if (innerValue !== value) {
				setInnerValue(value);
			}
		}, [visible]);
		useEffect(() => {
			if (!visible) {
				setInnerValue(value);
			}
		}, [value]);

		const onChange = useMemoizedFn((val, ext) => {
			setInnerValue(val);
			if (visible) {
				props.onSelect?.(val, ext);
			}
		});

		const pickerElement = withNativeProps(
			props,
			<div className={styles.rfPicker}>
				<div className={styles.rfPicker_body}>
					<PickerView
						loading={props.loading}
						loadingContent={props.loadingContent}
						columns={props.columns}
						renderLabel={props.renderLabel}
						value={innerValue}
						mouseWheel={props.mouseWheel}
						onChange={onChange}
					/>
				</div>
			</div>,
		);

		const popupElement = (
			<Popup
				style={props.popupStyle}
				className={classNames(styles.rfPicker_popup, props.popupClassName)}
				visible={visible}
				position="bottom"
				onMaskClick={() => {
					if (!props.closeOnMaskClick) return;
					props.onCancel?.();
					setVisible(false);
				}}
				getContainer={props.getContainer}
				destroyOnClose={props.destroyOnClose}
				afterShow={props.afterShow}
				afterClose={props.afterClose}
				onClick={props.onClick}
				forceRender={props.forceRender}
				stopPropagation={props.stopPropagation}
			>
				{pickerElement}
				<div className="w-full px-3 pb-8">
					<a
						className="flex h-12 w-full items-center justify-center rounded-full 
            border-1 px-4 text-sm font-semibold dark:border-white dark:text-f1ece8"
						onClick={() => {
							if (props.loading) return;
							setValue(innerValue, true);
							setVisible(false);
						}}
					>
						{t("confirm")}
					</a>
				</div>
				<SafeArea position="bottom" />
			</Popup>
		);

		return (
			<>
				{popupElement}
				{props.children?.(extend.items, actions)}
			</>
		);
	}),
);

Picker.displayName = "Picker";
